import { Spacing } from '@persona-id/design-tokens';
import { intersection } from 'lodash/fp';
import { WrappedFieldArrayProps } from 'redux-form';
import styled from 'styled-components';

import { DEFAULT_DOCUMENT_ALLOWED_IMAGES } from 'dashboard/lib/stubs/verification-template-version';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import Tooltip from 'design-system/components/Tooltip';
import { DocumentAllowedFileType } from 'lib/interfaces/StepsConfig';

type ToggleLabels = Record<string, { title?: string; description: string }>;

const ToggleRowWrapper = styled('div')<{ subToggle: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => (props.subToggle ? '0px' : `${Spacing[4]}`)} 0;

  // top padding from section header
  &:nth-child(2) {
    margin-top: ${Spacing[3]};
  }

  // begin rendering border after initial toggle row
  :not(:first-of-type) {
    border-top: 1px solid
      ${(props) => (props.subToggle ? 'none' : props.theme.color.border.secondary)};
  }

  & + & {
    margin-top: ${Spacing[2]};
  }
`;

const ToggleLabelContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-right: auto;
  align-self: flex-start;
  justify-content: space-between;
`;

const StyledDescription = styled(Paragraph).attrs(() => ({ size: 'md' }))<{ subToggle: boolean }>`
  color: ${(props) =>
    props.subToggle ? props.theme.color.text.primary : props.theme.color.text.tertiary};
`;

const ToggleContainer = styled('div')`
  height: 100%;
  margin-left: auto;
  margin-bottom: auto;
  align-self: flex-end;
`;

const ErrorMessage = styled('div')`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  margin-top: ${Spacing[2]};
`;

const TOOLTIP_MESSAGES: Record<string, string> = {
  web_camera: 'Guided capture is not supported if only PDFs are accepted',
  mobile_camera: 'Unguided capture is not supported if only PDFs are accepted',
};

const ToggleRowFields = (
  props: WrappedFieldArrayProps & {
    options: string[];
    labels: ToggleLabels;
    disabled: boolean;
    subToggle?: boolean;
    enabledFileTypes?: DocumentAllowedFileType[];
  },
) => {
  const {
    fields,
    options,
    labels,
    subToggle,
    disabled,
    meta,
    enabledFileTypes,
    ...passthroughProps
  } = props;

  return (
    <>
      {options.map((option, idx) => {
        const title = labels[option].title;
        const description = labels[option].description;
        const currentFields = fields.getAll();
        const value = currentFields.includes(option);
        const captureOptionDisabled =
          disabled ||
          (enabledFileTypes != null &&
            (option === 'web_camera' || option === 'mobile_camera') &&
            !intersection(enabledFileTypes, DEFAULT_DOCUMENT_ALLOWED_IMAGES).length);

        return (
          // favor de-emphasized description text style for sub toggles
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          <ToggleRowWrapper key={idx} subToggle={subToggle}>
            <ToggleLabelContainer>
              {!subToggle && (
                <Label size='md' weight='bold'>
                  {title}
                </Label>
              )}
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <StyledDescription subToggle={subToggle}>{description}</StyledDescription>
            </ToggleLabelContainer>
            <ToggleContainer>
              <Tooltip
                side='top'
                portal={false}
                disabled={!captureOptionDisabled}
                trigger={
                  <span>
                    <ToggleSlider
                      data-test={`toggle-row-${fields.name}-${idx}__slider`}
                      value={value}
                      {...passthroughProps}
                      onChange={(enabled) => {
                        if (enabled) {
                          fields.push(option);
                        } else {
                          const index = currentFields.indexOf(option, 0);
                          if (index > -1) {
                            fields.remove(index);
                          }
                        }
                      }}
                      disabled={captureOptionDisabled}
                      labelOff='Off'
                      labelOn='On'
                    />
                  </span>
                }
              >
                <Paragraph>{TOOLTIP_MESSAGES[option]}</Paragraph>
              </Tooltip>
            </ToggleContainer>
          </ToggleRowWrapper>
        );
      })}
      {meta.invalid && <ErrorMessage>{meta.error}</ErrorMessage>}
    </>
  );
};

export default ToggleRowFields;
